import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchDwgChildPdfForPreview } from "@/stores/documents/repository";
import { isIfcFile } from "@/domains/documents/constants/fileTypes";
import type { Document } from "@/types/documents";

/**
 * Minimal document data needed to open the viewer.
 * Works with both Document and DocumentRowMapped types.
 */
export interface ViewerDocumentData {
  id: number | string;
  filename: string;
  file_path: string;
  building_id: number | string;
  content_type: string | null;
  size: number | null;
  created_at: string;
  parent_document_id?: number | string | null;
  version?: number | null;
  created_by_name?: string | null;
}

export interface UseDocumentViewerReturn {
  /** Whether the viewer dialog is open */
  isOpen: boolean;
  /** The document being previewed (converted to Document type) */
  document: Document | null;
  /** Blob URL for the document content */
  blobUrl: string | null;
  /** Raw blob data for Office file rendering */
  blobData: Blob | null;
  /** Original DWG file path (for download option on DWG files) */
  originalDwgFilePath: string | null;
  /** Whether the document is currently loading */
  isLoading: boolean;
  /** Open the viewer for a document */
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Close the viewer and cleanup */
  closeViewer: () => void;
  /** Set open state (for controlled dialog) */
  setIsOpen: (open: boolean) => void;
}

/**
 * useDocumentViewer - Reusable hook for document viewing functionality
 *
 * Handles:
 * - Loading document blob from Supabase storage
 * - DWG file special handling (loads child PDF for preview)
 * - Blob URL lifecycle management
 * - Error handling with toast notifications
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const viewer = useDocumentViewer();
 *
 *   return (
 *     <>
 *       <Button onClick={() => viewer.openViewer(doc)}>View</Button>
 *       <DocumentViewerDialog
 *         document={viewer.document}
 *         blobUrl={viewer.blobUrl}
 *         open={viewer.isOpen}
 *         onOpenChange={viewer.setIsOpen}
 *         originalDwgFilePath={viewer.originalDwgFilePath}
 *       />
 *     </>
 *   );
 * }
 * ```
 */
export function useDocumentViewer(): UseDocumentViewerReturn {
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [document, setDocument] = useState<Document | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobData, setBlobData] = useState<Blob | null>(null);
  const [originalDwgFilePath, setOriginalDwgFilePath] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Cleanup blob URL when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const closeViewer = useCallback(() => {
    setIsOpen(false);
    // Cleanup blob URL when closing
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setBlobData(null);
    setDocument(null);
    setOriginalDwgFilePath(null);
  }, [blobUrl]);

  const openViewer = useCallback(
    async (doc: ViewerDocumentData) => {
      setIsLoading(true);

      // Cleanup previous blob URL if any
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }

      // WHA-3604: an .ifc preview only opens a small "open in 3D viewer?" modal,
      // so there's nothing to download — skip the (potentially large) blob fetch
      // and open the dialog immediately with minimal metadata.
      if (isIfcFile(doc.filename)) {
        setBlobData(null);
        setOriginalDwgFilePath(null);
        setDocument({
          id: String(doc.id),
          filename: doc.filename,
          file_path: doc.file_path,
          building_id: String(doc.building_id),
          content_type: doc.content_type || "application/octet-stream",
          size: doc.size || 0,
          created_at: doc.created_at,
          parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
          version: doc.version ?? undefined,
          created_by_name: doc.created_by_name ?? undefined,
        });
        setIsOpen(true);
        setIsLoading(false);
        return;
      }

      const isDwgFile = doc.filename?.toLowerCase().endsWith(".dwg");
      let filePathToPreview = doc.file_path;
      let contentTypeForPreview = doc.content_type;

      // For DWG files, fetch the child PDF for preview
      if (isDwgFile) {
        try {
          const childPdf = await fetchDwgChildPdfForPreview(String(doc.id));
          if (!childPdf) {
            throw new Error("DWG preview not available - no child PDF found");
          }
          filePathToPreview = childPdf.file_path;
          contentTypeForPreview = childPdf.content_type || "application/pdf";
        } catch (error) {
          console.error("Failed to fetch DWG child PDF:", error);
          const errorMessage = error instanceof Error ? error.message : "Failed to load preview";
          toast({
            title: "Preview Unavailable",
            description: errorMessage,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      }

      // Load the document blob
      try {
        const { data, error } = await supabase.storage
          .from("documents")
          .download(filePathToPreview);

        if (error) {
          // Parse error message properly
          let errMsg = "File not available";
          if (typeof error === "string") {
            errMsg = error;
          } else if (typeof error === "object" && error !== null) {
            const errObj = error as Record<string, unknown>;
            if (typeof errObj.message === "string" && errObj.message) {
              errMsg = errObj.message;
            } else if (typeof errObj.error === "string" && errObj.error) {
              errMsg = errObj.error;
            }
          }
          throw new Error(errMsg);
        }

        // Create blob URL
        const blob = new Blob([data], {
          type: contentTypeForPreview || "application/octet-stream",
        });
        const newBlobUrl = URL.createObjectURL(blob);

        // Convert to Document type for DocumentViewerDialog
        const documentForPreview: Document = {
          id: String(doc.id),
          filename: doc.filename,
          file_path: filePathToPreview,
          building_id: String(doc.building_id),
          content_type: contentTypeForPreview || "application/octet-stream",
          size: doc.size || 0,
          created_at: doc.created_at,
          parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
          version: doc.version ?? undefined,
          created_by_name: doc.created_by_name ?? undefined,
        };

        // Set state and open dialog
        setBlobUrl(newBlobUrl);
        setBlobData(blob);
        setDocument(documentForPreview);
        setOriginalDwgFilePath(isDwgFile ? doc.file_path : null);
        setIsOpen(true);
      } catch (error) {
        console.error("Failed to load document:", error);
        toast({
          title: "Unable to preview",
          description: "This file is no longer available.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [blobUrl, toast]
  );

  // Handle controlled dialog state changes
  const handleSetIsOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        closeViewer();
      } else {
        setIsOpen(true);
      }
    },
    [closeViewer]
  );

  return {
    isOpen,
    document,
    blobUrl,
    blobData,
    originalDwgFilePath,
    isLoading,
    openViewer,
    closeViewer,
    setIsOpen: handleSetIsOpen,
  };
}
